import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useHospitalData } from '../../hooks/useHospitalData'
import LineChart, { LineChartHandle } from '../../components/Charts/LineChart'
import CustomSelect from '../../components/Common/CustomSelect'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { sortTimePeriods, trimTrailingEmptyPeriods, filterFutureMonths, getStratificationColors, normalizeStratGroup, createChartExportHandlers, createMenuToggle, setupMenuClickOutside } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './Screening.css'

const Screening: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()
    const { data, loading, initialLoading, error } = useHospitalData(
        '/api/pmh/process/screening',
        selectedHospitalIds
    )

    const safeParseEducationPct = (value: any): number | null => {
        if (value === null || value === undefined || value === '' || value === '""') {
            return null
        }

        if (value === 0 || value === "0") {
            return 0
        }

        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    const [mainPeriod, setMainPeriod] = usePersistedState('pmh_screening_mainPeriod', 'delivery')
    const [mainInterval, setMainInterval] = usePersistedState('pmh_screening_mainInterval', 'monthly')
    const [mainChartData, setMainChartData] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [mainNoDataMessage, setMainNoDataMessage] = useState<string | undefined>(undefined)

    const [stratPeriod, setStratPeriod] = usePersistedState('pmh_screening_stratPeriod', 'delivery')
    const [stratMeasure, setStratMeasure] = usePersistedState('pmh_screening_stratMeasure', 'anxiety')
    const [stratType, setStratType] = usePersistedState('pmh_screening_stratType', 'insurance')
    const [stratInterval, setStratInterval] = usePersistedState('pmh_screening_stratInterval', 'monthly')
    const [stratChartData, setStratChartData] = useState<{ categories: string[], series: any[], colors: string[] }>({ categories: [], series: [], colors: [] })
    const [stratNoDataMessage, setStratNoDataMessage] = useState<string | undefined>(undefined)

    const mainChartRef = useRef<LineChartHandle>(null)
    const mainChartContainerRef = useRef<HTMLDivElement>(null)
    const stratChartRef = useRef<LineChartHandle>(null)
    const stratChartContainerRef = useRef<HTMLDivElement>(null)

    const mainMenuId = 'export-menu-screening-main'
    const toggleMainMenu = createMenuToggle(mainMenuId)
    const mainChartExports = createChartExportHandlers(
        mainChartContainerRef,
        'PMH Screening',
        'PMH_Screening_Overall',
        { fontBoost: true, exportTitle: 'PMH Screening' }
    )

    const stratMenuId = 'export-menu-screening-strat'
    const toggleStratMenu = createMenuToggle(stratMenuId)
    const stratChartExports = createChartExportHandlers(
        stratChartContainerRef,
        'PMH Screening - Stratified',
        'PMH_Screening_Stratified',
        { fontBoost: true, exportTitle: 'PMH Screening - Stratified' }
    )

    useEffect(() => {
        const cleanup1 = setupMenuClickOutside(() => mainChartExports.closeMenu(mainMenuId));
        const cleanup2 = setupMenuClickOutside(() => stratChartExports.closeMenu(stratMenuId));
        return () => {
            cleanup1();
            cleanup2();
        };
    }, []);

    useEffect(() => {
        if (!data) {
            setMainChartData({ categories: [], series: [] })
            setMainNoDataMessage("No Data Available")
            return
        }

        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []

        if (rows.length === 0) {
            setMainChartData({ categories: [], series: [] })
            setMainNoDataMessage("No Data Available")
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 1
        const reportTypeIdx = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 2
        const stratIdx = columns.indexOf('stratification') !== -1 ? columns.indexOf('stratification') : 3
        const stratValueIdx = columns.indexOf('strat_value') !== -1 ? columns.indexOf('strat_value') : 4

        const filteredRows = rows.filter((row: any[]) => {
            return row[reportTypeIdx] === intervalMap[mainInterval] &&
                row[stratIdx] === 'Overall' &&
                row[stratValueIdx] === 'All Patients'
        })

        if (filteredRows.length === 0) {
            setMainChartData({ categories: [], series: [] })
            setMainNoDataMessage("No Data Available")
            return
        }

        const aggregatedData: Record<string, { anxiety: (number | null)[], depression: (number | null)[] }> = {}

        const anxDeliveryIdx = columns.indexOf('anxiety_delivery_pct') !== -1 ? columns.indexOf('anxiety_delivery_pct') : 10
        const depDeliveryIdx = columns.indexOf('depression_delivery_pct') !== -1 ? columns.indexOf('depression_delivery_pct') : 11
        const anxPrenatalIdx = columns.indexOf('anxiety_prenatal_pct') !== -1 ? columns.indexOf('anxiety_prenatal_pct') : 12
        const depPrenatalIdx = columns.indexOf('depression_prenatal_pct') !== -1 ? columns.indexOf('depression_prenatal_pct') : 13

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[periodIdx])

            if (timePeriod) {
                let val1: number | null, val2: number | null

                if (mainPeriod === 'delivery') {
                    val1 = safeParseEducationPct(row[anxDeliveryIdx])
                    val2 = safeParseEducationPct(row[depDeliveryIdx])
                } else {
                    val1 = safeParseEducationPct(row[anxPrenatalIdx])
                    val2 = safeParseEducationPct(row[depPrenatalIdx])
                }

                if (!aggregatedData[timePeriod]) aggregatedData[timePeriod] = { anxiety: [], depression: [] }
                aggregatedData[timePeriod].anxiety.push(val1)
                aggregatedData[timePeriod].depression.push(val2)
            }
        })

        const finalPeriods = filterFutureMonths(sortTimePeriods(Object.keys(aggregatedData), mainInterval))

        const anxietySeries: (number | null)[] = []
        const depressionSeries: (number | null)[] = []

        finalPeriods.forEach(period => {
            const periodDatum = aggregatedData[period]
            if (!periodDatum) return

            const anxVals = periodDatum.anxiety.filter(v => v !== null) as number[]
            const depVals = periodDatum.depression.filter(v => v !== null) as number[]

            const anxAvg = anxVals.length ? anxVals.reduce((a, b) => a + b, 0) / anxVals.length : null
            const depAvg = depVals.length ? depVals.reduce((a, b) => a + b, 0) / depVals.length : null

            anxietySeries.push(anxAvg !== null ? Math.round(anxAvg * 10) / 10 : null)
            depressionSeries.push(depAvg !== null ? Math.round(depAvg * 10) / 10 : null)
        })

        const hasValidData = anxietySeries.some(v => v !== null) || depressionSeries.some(v => v !== null)

        if (!hasValidData) {
            setMainChartData({
                categories: finalPeriods,
                series: [
                    { name: 'Anxiety Screening', data: finalPeriods.map(() => null) },
                    { name: 'Depression Screening', data: finalPeriods.map(() => null) }
                ]
            })
            setMainNoDataMessage("No Data Available")
            return
        }

        setMainNoDataMessage(undefined)
        const trimmedMain = trimTrailingEmptyPeriods(finalPeriods, [
            { name: 'Anxiety Screening', data: anxietySeries },
            { name: 'Depression Screening', data: depressionSeries }
        ])
        setMainChartData({
            categories: trimmedMain.categories,
            series: trimmedMain.series
        })

    }, [data, mainPeriod, mainInterval, isMultiSelect])

    useEffect(() => {
        if (!data) {
            setStratChartData({ categories: [], series: [], colors: [] })
            setStratNoDataMessage("No Data Available")
            return
        }

        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []

        if (rows.length === 0) {
            setStratChartData({ categories: [], series: [], colors: [] })
            setStratNoDataMessage("No Data Available")
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const stratMap: Record<string, string> = {
            'insurance': 'Insurance',
            'race': 'Race/Ethnicity',
            'race_all': 'Race/Ethnicity (Detailed)',
            'language': 'Primary Language'
        }

        const reportTypeIdx = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 2
        const stratColIdx = columns.indexOf('stratification') !== -1 ? columns.indexOf('stratification') : 3
        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 1
        const stratValueColIdx = columns.indexOf('strat_value') !== -1 ? columns.indexOf('strat_value') : 4
        const denominatorIdx = columns.indexOf('denominator') !== -1 ? columns.indexOf('denominator') : 5

        const filteredRows = rows.filter((row: any[]) => {
            return row[reportTypeIdx] === intervalMap[stratInterval] &&
                row[stratColIdx] === stratMap[stratType]
        })

        if (filteredRows.length === 0) {
            setStratChartData({ categories: [], series: [], colors: [] })
            setStratNoDataMessage("No Data Available")
            return
        }

        const aggregatedData: Record<string, Record<string, { numerator: number, denominator: number }>> = {}
        const allPeriods = new Set<string>()

        const anxDeliveryNumIdx = columns.indexOf('anxiety_delivery_numerator') !== -1 ? columns.indexOf('anxiety_delivery_numerator') : 6
        const depDeliveryNumIdx = columns.indexOf('depression_delivery_numerator') !== -1 ? columns.indexOf('depression_delivery_numerator') : 7
        const anxPrenatalNumIdx = columns.indexOf('anxiety_prenatal_numerator') !== -1 ? columns.indexOf('anxiety_prenatal_numerator') : 8
        const depPrenatalNumIdx = columns.indexOf('depression_prenatal_numerator') !== -1 ? columns.indexOf('depression_prenatal_numerator') : 9

        let numeratorIdx: number
        if (stratPeriod === 'delivery' && stratMeasure === 'anxiety') {
            numeratorIdx = anxDeliveryNumIdx
        } else if (stratPeriod === 'delivery' && stratMeasure === 'depression') {
            numeratorIdx = depDeliveryNumIdx
        } else if (stratPeriod === 'prenatal' && stratMeasure === 'anxiety') {
            numeratorIdx = anxPrenatalNumIdx
        } else {
            numeratorIdx = depPrenatalNumIdx
        }

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[periodIdx])
            const rawGroupName = String(row[stratValueColIdx])
            const groupName = normalizeStratGroup(stratType, rawGroupName) || rawGroupName
            const denominator = safeParseEducationPct(row[denominatorIdx])
            const numerator = safeParseEducationPct(row[numeratorIdx])

            if (timePeriod && groupName && denominator !== null) {
                allPeriods.add(timePeriod)

                if (!aggregatedData[timePeriod]) aggregatedData[timePeriod] = {}
                if (!aggregatedData[timePeriod][groupName]) {
                    aggregatedData[timePeriod][groupName] = { numerator: 0, denominator: 0 }
                }

                aggregatedData[timePeriod][groupName].denominator += denominator
                if (numerator !== null) {
                    aggregatedData[timePeriod][groupName].numerator += numerator
                }
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Array.from(allPeriods), stratInterval))
        const series: any[] = []

        const groupNames = Object.keys(aggregatedData).flatMap(period => Object.keys(aggregatedData[period]))
        const uniqueGroupNames = Array.from(new Set(groupNames))

        let sortedGroupNames: string[]
        if (stratType === 'insurance') {
            sortedGroupNames = ['Private', 'Public'].filter(g => uniqueGroupNames.includes(g))
        } else if (stratType === 'race') {
            sortedGroupNames = ['BIPOC', 'White'].filter(g => uniqueGroupNames.includes(g))
        } else if (stratType === 'race_all') {
            sortedGroupNames = ['Black/African American', 'White', 'Hispanic', 'Asian', 'Race Not Documented', 'Other']
        } else {
            sortedGroupNames = uniqueGroupNames.sort()
        }

        sortedGroupNames.forEach(group => {
            const groupData = sortedPeriods.map(period => {
                const data = aggregatedData[period]?.[group]
                if (data && data.denominator > 0) {
                    return Math.round(((data.numerator / data.denominator) * 100) * 10) / 10
                }
                return null
            })
            series.push({ name: group, data: groupData })
        })

        const colors = getStratificationColors(stratType)

        const allNull = series.length > 0 && series.every(s => s.data.every((v: any) => v === null))

        if (allNull) {
            setStratChartData({ categories: sortedPeriods, series, colors })
            setStratNoDataMessage("No Data Available")
            return
        }

        setStratNoDataMessage(undefined)
        const trimmedStrat = trimTrailingEmptyPeriods(sortedPeriods, series)
        setStratChartData({
            categories: trimmedStrat.categories,
            series: trimmedStrat.series,
            colors: colors
        })

    }, [data, stratPeriod, stratMeasure, stratType, stratInterval, isMultiSelect])

    if (initialLoading) return <LoadingNotice title="Loading screening data..." />

    return (
        <div id={generatePDFIds.dashboard()} className="screening-page">
            <div className="page-header">
                <h1 className="page-title">Process Measures</h1>
                <div className="header-controls">
                    <CustomSelect
                        value={mainPeriod}
                        onChange={setMainPeriod}
                        options={[
                            { value: 'delivery', label: 'Delivery Hospitalization' },
                            { value: 'prenatal', label: 'Prenatally' }
                        ]}
                    />
                    <CustomSelect
                        value={mainInterval}
                        onChange={setMainInterval}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div id={generatePDFIds.chart(1)} className="chart-section" ref={mainChartContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Screening</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMainMenu}>&#9776;</button>
                        <div id={mainMenuId} className="export-dropdown">
                            <div onClick={() => { mainChartExports.handleDownloadSVG(mainMenuId); }}>Download SVG</div>
                            <div onClick={() => { mainChartExports.handleDownloadPNG(mainMenuId); }}>Download PNG</div>
                            <div onClick={() => { mainChartRef.current?.exportToCSV(); mainChartExports.closeMenu(mainMenuId); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of sample patient charts that had documentation of PMH screening using a validated tool.
                </p>
                <LineChart
                    ref={mainChartRef}
                    categories={mainChartData.categories}
                    series={mainChartData.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Screening %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={mainNoDataMessage}
                    showXAxisTooltip={true}
                    isLoading={loading}
                    decimals={1}
                    exportFilename="PMH_Screening_Overall"
                />
                <p className="data-source">Data from hospital level Data form</p>
            </div>

            <div className="strat-controls-container">
                <CustomSelect
                    value={stratPeriod}
                    onChange={setStratPeriod}
                    options={[
                        { value: 'delivery', label: 'Delivery Hospitalization' },
                        { value: 'prenatal', label: 'Prenatally' }
                    ]}
                />
                <CustomSelect
                    value={stratMeasure}
                    onChange={setStratMeasure}
                    options={[
                        { value: 'anxiety', label: 'Anxiety' },
                        { value: 'depression', label: 'Depression' }
                    ]}
                />
                <CustomSelect
                    value={stratType}
                    onChange={setStratType}
                    options={[
                        { value: 'insurance', label: 'Insurance Status' },
                        { value: 'race', label: 'Race/Ethnicity (BIPOC & WHITE)' },
                        { value: 'race_all', label: 'Race/Ethnicity (All)' },
                        { value: 'language', label: 'Primary Language' }
                    ]}
                />
                <CustomSelect
                    value={stratInterval}
                    onChange={setStratInterval}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(2)} className="chart-section" ref={stratChartContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Screening - Stratified</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleStratMenu}>&#9776;</button>
                        <div id={stratMenuId} className="export-dropdown">
                            <div onClick={() => { stratChartExports.handleDownloadSVG(stratMenuId); }}>Download SVG</div>
                            <div onClick={() => { stratChartExports.handleDownloadPNG(stratMenuId); }}>Download PNG</div>
                            <div onClick={() => { stratChartRef.current?.exportToCSV(); stratChartExports.closeMenu(stratMenuId); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of sample patient charts that had documentation of PMH screening using a validated tool stratified by {stratType.replace('_', ' ')}.
                </p>
                <LineChart
                    ref={stratChartRef}
                    categories={stratChartData.categories}
                    series={stratChartData.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Screening %"
                    xAxisLabel="Time Period"
                    colors={stratChartData.colors}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={stratNoDataMessage}
                    showXAxisTooltip={true}
                    isLoading={loading}
                    decimals={1}
                    exportFilename="PMH_Screening_Stratified"
                />
                <p className="data-source">Data from hospital level Data form</p>
            </div>
        </div>
    )
}

export default Screening
